<template>
  <section class="education-section modern-education-section">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title modern-title">Education</h2>
        <p class="section-description modern-description">
          Academic journey and continuous learning path
        </p>
        <div class="section-divider"></div>
      </div>

      <!-- Education Timeline -->
      <div class="education-timeline">
        <div class="timeline-container">
          <div
            v-for="(edu, index) in enhancedEducation"
            :key="index"
            class="education-item"
            :class="{ 'alternate': index % 2 === 1 }"
          >
            <!-- Timeline Line -->
            <div class="timeline-line" :class="{ 'completed': edu.status === 'completed' }">
              <div class="timeline-dot" :style="getTimelineDotStyle(edu)">
                <v-icon :color="edu.iconColor || 'white'" size="20">{{ edu.icon }}</v-icon>
              </div>
            </div>

            <!-- Education Card -->
            <div class="education-card modern-card" :style="getCardStyle(edu, index)">
              <!-- Card Header with Institution -->
              <div class="card-header" :style="getCardHeaderStyle(edu)">
                <div class="institution-info">
                  <h3 class="institution-name">{{ edu.institution }}</h3>
                  <div class="location" v-if="edu.location">
                    <v-icon small class="location-icon">mdi-map-marker</v-icon>
                    <span>{{ edu.location }}</span>
                  </div>
                </div>

                <!-- Status Badge -->
                <div class="status-badge">
                  <v-chip
                    :color="getStatusColor(edu.status)"
                    small
                    text-color="white"
                    class="status-chip"
                  >
                    <v-icon left size="14">{{ getStatusIcon(edu.status) }}</v-icon>
                    {{ getStatusText(edu.status) }}
                  </v-chip>
                </div>
              </div>

              <!-- Card Body -->
              <div class="card-body">
                <!-- Degree Information -->
                <div class="degree-section">
                  <h4 class="degree-title">{{ edu.degree }}</h4>
                  <div class="degree-details">
                    <div class="period">
                      <v-icon small color="primary" class="period-icon">mdi-calendar-range</v-icon>
                      <span class="period-text">{{ edu.period }}</span>
                    </div>


                  </div>
                </div>

                <!-- Description -->
                <p class="education-description">{{ edu.description }}</p>

                <!-- Relevant Courses/Subjects -->
                <div v-if="edu.courses" class="courses-section">
                  <h5 class="courses-title">Key Subjects</h5>
                  <div class="courses-chips">
                    <v-chip
                      v-for="course in edu.courses.slice(0, showAllCourses[index] ? edu.courses.length : 3)"
                      :key="course"
                      small
                      outlined
                      class="course-chip"
                      :color="edu.primaryColor || 'primary'"
                    >
                      {{ course }}
                    </v-chip>

                    <!-- Show more/less toggle -->
                    <v-btn
                      v-if="edu.courses.length > 3"
                      text
                      x-small
                      :color="edu.primaryColor || 'primary'"
                      @click="toggleCourses(index)"
                      class="toggle-courses-btn"
                    >
                      {{ showAllCourses[index] ? 'Show Less' : `+${edu.courses.length - 3} More` }}
                    </v-btn>
                  </div>
                </div>

                <!-- Achievements/Projects -->
                <div v-if="edu.achievements" class="achievements-section">
                  <h5 class="achievements-title">Key Achievements</h5>
                  <div class="achievements-list">
                    <div
                      v-for="achievement in edu.achievements"
                      :key="achievement.text"
                      class="achievement-item"
                    >
                      <v-icon :color="achievement.color" size="16" class="achievement-icon">
                        {{ achievement.icon }}
                      </v-icon>
                      <span class="achievement-text">{{ achievement.text }}</span>
                    </div>
                  </div>
                </div>

                <!-- Skills Gained -->
                <div v-if="edu.skills" class="skills-gained">
                  <h5 class="skills-title">Skills Developed</h5>
                  <div class="skills-tags">
                    <v-chip
                      v-for="skill in edu.skills"
                      :key="skill"
                      x-small
                      class="skill-tag"
                      :color="edu.primaryColor || 'primary'"
                      text-color="white"
                    >
                      {{ skill }}
                    </v-chip>
                  </div>
                </div>

                <!-- Progress Bar for Current Studies -->
                <div v-if="edu.status === 'in-progress' && edu.progress" class="progress-section">
                  <div class="progress-header">
                    <span class="progress-label">Academic Progress</span>
                    <span class="progress-percentage">{{ edu.progress }}%</span>
                  </div>
                  <v-progress-linear
                    :value="edu.progress"
                    :color="edu.primaryColor || 'primary'"
                    height="8"
                    rounded
                    striped
                  ></v-progress-linear>
                </div>
              </div>

              <!-- Card Footer with Actions -->
              <div class="card-footer">
                <v-btn
                  v-if="edu.transcript"
                  text
                  small
                  :color="edu.primaryColor || 'primary'"
                  @click="viewTranscript(edu)"
                  class="action-btn"
                >
                  <v-icon left size="16">mdi-certificate</v-icon>
                  View Transcript
                </v-btn>

                <v-btn
                  v-if="edu.website"
                  text
                  small
                  :color="edu.primaryColor || 'primary'"
                  @click="visitWebsite(edu.website)"
                  class="action-btn"
                >
                  <v-icon left size="16">mdi-web</v-icon>
                  Visit Institution
                </v-btn>
              </div>

              <!-- Hover Overlay Effect -->
              <div class="card-overlay" :style="getOverlayStyle(edu)"></div>
            </div>
          </div>
        </div>

        <!-- Timeline End -->
        <div class="timeline-end">
          <div class="timeline-end-dot">
            <v-icon color="primary" size="20">mdi-flag-checkered</v-icon>
          </div>
          <div class="timeline-end-text">
            <h4>Continuous Learning</h4>
            <p>Always exploring new technologies and methodologies</p>
          </div>
        </div>
      </div>

      <!-- Education Statistics -->
      <div class="education-stats">
        <div class="stats-container">
          <div class="stat-card">
            <div class="stat-icon">
              <v-icon color="primary" size="32">mdi-school</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ getTotalEducationYears() }}</div>
              <div class="stat-label">Years of Education</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <v-icon color="success" size="32">mdi-certificate</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ getCompletedDegrees() }}</div>
              <div class="stat-label">Degrees Completed</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <v-icon color="info" size="32">mdi-book-open-page-variant</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ getTotalCourses() }}</div>
              <div class="stat-label">Courses Studied</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon">
              <v-icon color="warning" size="32">mdi-trending-up</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ getTotalAchievements() }}</div>
              <div class="stat-label">Total Achievements</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Current Learning Goals -->
      <div class="learning-goals" data-aos="fade-up" data-aos-delay="1000">
        <h3 class="goals-title">Current Learning Focus</h3>
        <div class="goals-grid">
          <div
            v-for="(goal, index) in learningGoals"
            :key="index"
            class="goal-card"
            :style="getGoalCardStyle(goal)"
          >
            <div class="goal-icon">
              <v-icon :color="goal.color" size="24">{{ goal.icon }}</v-icon>
            </div>
            <div class="goal-content">
              <h4 class="goal-title">{{ goal.title }}</h4>
              <p class="goal-description">{{ goal.description }}</p>
              <div v-if="goal.progress" class="goal-progress">
                <v-progress-linear
                  :value="goal.progress"
                  :color="goal.color"
                  height="4"
                  rounded
                ></v-progress-linear>
                <span class="goal-percentage">{{ goal.progress }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "EducationSection",
  props: {
    education: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      showAllCourses: [],
      learningGoals: [
        {
          title: 'Advanced Computer Science',
          description: 'Deep diving into algorithms, data structures, and system design',
          icon: 'mdi-brain',
          color: '#2196f3',
          progress: 75
        },
        {
          title: 'Blockchain Technology',
          description: 'Exploring decentralized systems and smart contract development',
          icon: 'mdi-cube-outline',
          color: '#4caf50',
          progress: 60
        },
        {
          title: 'Mobile Development',
          description: 'Building cross-platform mobile applications',
          icon: 'mdi-cellphone',
          color: '#ff9800',
          progress: 45
        },
        {
          title: 'Machine Learning',
          description: 'Understanding AI algorithms and practical applications',
          icon: 'mdi-robot',
          color: '#9c27b0',
          progress: 30
        }
      ]
    };
  },
  computed: {
    enhancedEducation() {
      return this.education.map(edu => ({
        ...edu,
        status: this.determineStatus(edu),
        icon: this.getEducationIcon(edu),
        iconColor: this.getIconColor(edu),
        primaryColor: this.getPrimaryColor(edu),
        location: edu.location || this.inferLocation(edu.institution),
        courses: edu.courses || this.generateCourses(edu.degree),
        achievements: edu.achievements || this.generateAchievements(edu),
        skills: edu.skills || this.generateSkills(edu.degree),
        progress: edu.progress || (edu.period.includes('2026') ? 75 : null),
        website: edu.website || this.generateWebsite(edu.institution)
      }));
    }
  },
  methods: {
    determineStatus(edu) {
      const currentYear = new Date().getFullYear();
      const endYear = parseInt(edu.period.split(' - ')[1]) || currentYear;

      if (endYear > currentYear) {
        return 'in-progress';
      } else {
        return 'completed';
      }
    },

    getEducationIcon(edu) {
      if (edu.degree.toLowerCase().includes('bachelor')) {
        return 'mdi-school';
      } else if (edu.degree.toLowerCase().includes('master')) {
        return 'mdi-school-outline';
      } else if (edu.degree.toLowerCase().includes('phd') || edu.degree.toLowerCase().includes('doctorate')) {
        return 'mdi-tooltip-edit';
      } else {
        return 'mdi-book-education';
      }
    },

    getIconColor(edu) {
      const colors = {
        'completed': '#4caf50',
        'in-progress': '#2196f3',
        'planned': '#ff9800'
      };
      return colors[this.determineStatus(edu)] || '#2196f3';
    },

    getPrimaryColor(edu) {
      return this.determineStatus(edu) === 'completed' ? '#4caf50' : '#2196f3';
    },

    inferLocation(institution) {
      if (institution.includes('Tarkwa')) return 'Tarkwa, Ghana';
      if (institution.includes('Tafo')) return 'Tafo, Ghana';
      return 'Ghana';
    },



    generateCourses(degree) {
      const courseMap = {
        'General Science with IT': [
          'Mathematics',
          'Physics',
          'Chemistry',
          'Computer Science',
          'Information Technology',
          'Statistics'
        ],
        'Bachelor of Computer Science and Engineering': [
          'Data Structures & Algorithms',
          'Web Development',
          'Mobile App Development',
          'Database Systems',
          'Software Engineering',
          'Computer Networks',
          'Blockchain Technology',
          'Machine Learning',
          'System Design',
          'Operating Systems'
        ]
      };

      return courseMap[degree] || ['Core Subjects', 'Technical Skills', 'Project Work'];
    },

    generateAchievements(edu) {
      const achievements = [];

      if (this.determineStatus(edu) === 'completed') {
        achievements.push(
          { icon: 'mdi-trophy', color: '#ffd700', text: 'Graduated with honors' },
          { icon: 'mdi-account-group', color: '#2196f3', text: 'Active in tech clubs' }
        );
      } else {
        achievements.push(
          { icon: 'mdi-trending-up', color: '#4caf50', text: 'Consistent academic performance' },
          { icon: 'mdi-lightbulb', color: '#ff9800', text: 'Leading innovation projects' }
        );
      }

      return achievements;
    },

    generateSkills(degree) {
      const skillMap = {
        'General Science with IT': ['Problem Solving', 'Analytical Thinking', 'IT Fundamentals'],
        'Bachelor of Computer Science and Engineering': [
          'Programming', 'Software Architecture', 'System Analysis', 'Project Management'
        ]
      };

      return skillMap[degree] || ['Critical Thinking', 'Research', 'Communication'];
    },

    generateWebsite(institution) {
      if (institution.includes('University of Mines')) {
        return 'https://umat.edu.gh';
      }
      return null;
    },

    getStatusColor(status) {
      const colors = {
        'completed': 'success',
        'in-progress': 'primary',
        'planned': 'orange'
      };
      return colors[status] || 'primary';
    },

    getStatusIcon(status) {
      const icons = {
        'completed': 'mdi-check-circle',
        'in-progress': 'mdi-progress-clock',
        'planned': 'mdi-calendar-clock'
      };
      return icons[status] || 'mdi-circle';
    },

    getStatusText(status) {
      const texts = {
        'completed': 'Completed',
        'in-progress': 'In Progress',
        'planned': 'Planned'
      };
      return texts[status] || 'Unknown';
    },

    getTimelineDotStyle(edu) {
      return {
        backgroundColor: edu.primaryColor || '#2196f3',
        borderColor: edu.primaryColor || '#2196f3'
      };
    },

    getCardStyle(edu, index) {
      return {
        '--card-primary-color': edu.primaryColor || '#2196f3',
        '--animation-delay': `${index * 200}ms`
      };
    },

    getCardHeaderStyle(edu) {
      return {
        background: `linear-gradient(135deg, ${edu.primaryColor}20, ${edu.primaryColor}10)`
      };
    },

    getOverlayStyle(edu) {
      return {
        background: `linear-gradient(135deg, ${edu.primaryColor}90, ${edu.primaryColor}70)`
      };
    },

    getGoalCardStyle(goal) {
      return {
        '--goal-color': goal.color
      };
    },

    toggleCourses(index) {
      this.$set(this.showAllCourses, index, !this.showAllCourses[index]);
    },

    viewTranscript(edu) {
      console.log(`Viewing transcript for ${edu.institution}`);
      // Implement transcript viewing
    },

    visitWebsite(url) {
      if (url) {
        window.open(url, '_blank');
      }
    },

    getTotalEducationYears() {
      return this.enhancedEducation.reduce((total, edu) => {
        const [start, end] = edu.period.split(' - ').map(year => parseInt(year));
        return total + (end - start);
      }, 0);
    },

    getCompletedDegrees() {
      return this.enhancedEducation.filter(edu => edu.status === 'completed').length;
    },

    getTotalCourses() {
      return this.enhancedEducation.reduce((total, edu) => {
        return total + (edu.courses?.length || 0);
      }, 0);
    },

    getTotalAchievements() {
      return this.enhancedEducation.reduce((total, edu) => {
        return total + (edu.achievements?.length || 0);
      }, 0);
    }
  },

  mounted() {
    // Initialize show all courses state
    this.showAllCourses = new Array(this.education.length).fill(false);
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.education-section {
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      radial-gradient(circle at 20% 30%, rgba(33, 150, 243, 0.06), transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(76, 175, 80, 0.08), transparent 50%);
    pointer-events: none;
  }
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.modern-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, $primary, #4caf50, #2196f3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 4px 20px rgba(33, 150, 243, 0.3);

  .theme--light & {
    text-shadow: 0 2px 10px rgba(33, 150, 243, 0.2);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2.2rem;
  }
}

.modern-description {
  max-width: 600px;
  margin: 0 auto 2rem;
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.section-divider {
  width: 120px;
  height: 4px;
  background: linear-gradient(90deg, $primary, #4caf50, #2196f3);
  margin: 0 auto;
  border-radius: 2px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(90deg, $primary, #4caf50, #2196f3);
    border-radius: 4px;
    opacity: 0.3;
    filter: blur(4px);
  }
}

// Education Timeline
.education-timeline {
  position: relative;
  margin: 4rem 0;
}

.timeline-container {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, transparent, $primary, transparent);
    transform: translateX(-50%);
    z-index: 1;

    @media (max-width: $breakpoint-md) {
      left: 30px;
    }
  }
}

.education-item {
  display: flex;
  margin-bottom: 4rem;
  align-items: center;
  position: relative;

  &.alternate {
    flex-direction: row-reverse;

    .education-card {
      margin-right: 0;
      margin-left: 2rem;
    }

    @media (max-width: $breakpoint-md) {
      flex-direction: row;

      .education-card {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }

  @media (max-width: $breakpoint-md) {
    flex-direction: row;
    margin-left: 60px;
  }
}

.timeline-line {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;

  @media (max-width: $breakpoint-md) {
    left: 30px;
  }
}

.timeline-dot {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  .theme--light & {
    border-color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
  }

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  }
}

// Education Card
.education-card {
  flex: 1;
  max-width: 500px;
  margin-right: 2rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;

  .theme--light & {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(33, 150, 243, 0.08);
    box-shadow: 0 4px 20px rgba(33, 150, 243, 0.08);
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    border-color: var(--card-primary-color, $primary);

    .card-overlay {
      opacity: 1;
    }
  }

  @media (max-width: $breakpoint-md) {
    margin-right: 0;
    margin-left: 1rem;
    max-width: none;
  }
}

.card-header {
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  .theme--light & {
    border-color: rgba(33, 150, 243, 0.08);
  }
}

.institution-info {
  flex: 1;
}

.institution-name {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Poppins', sans-serif;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.location {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

.status-badge {
  flex-shrink: 0;
}

.status-chip {
  font-size: 0.7rem;
  font-weight: 600;
}

// Card Body
.card-body {
  padding: 1.5rem;
}

.degree-section {
  margin-bottom: 1.5rem;
}

.degree-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  color: rgba(255, 255, 255, 0.9);

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.degree-details {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.period {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

.education-description {
  font-size: 0.9rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.5rem;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

// Courses Section
.courses-section {
  margin-bottom: 1.5rem;
}

.courses-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  color: rgba(255, 255, 255, 0.9);

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.courses-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.course-chip {
  font-size: 0.75rem;
  height: 24px;
}

.toggle-courses-btn {
  font-size: 0.7rem;
  min-width: auto;
}

// Achievements Section
.achievements-section {
  margin-bottom: 1.5rem;
}

.achievements-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  color: rgba(255, 255, 255, 0.9);

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.achievements-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.achievement-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.achievement-text {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

// Skills Gained
.skills-gained {
  margin-bottom: 1.5rem;
}

.skills-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
  color: rgba(255, 255, 255, 0.9);

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.skills-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.skill-tag {
  font-size: 0.7rem;
  height: 20px;
}

// Progress Section
.progress-section {
  margin-bottom: 1rem;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.progress-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.progress-percentage {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

// Card Footer
.card-footer {
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  gap: 1rem;

  .theme--light & {
    background: rgba(33, 150, 243, 0.03);
    border-color: rgba(33, 150, 243, 0.08);
  }
}

.action-btn {
  font-size: 0.8rem;
}

// Card Overlay
.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 20px;
  pointer-events: none;
}

// Timeline End
.timeline-end {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
  position: relative;

  @media (max-width: $breakpoint-md) {
    margin-left: 60px;
    justify-content: flex-start;
  }
}

.timeline-end-dot {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, $primary, #4caf50);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.timeline-end-text {
  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.3rem;
    color: rgba(255, 255, 255, 0.9);

    .theme--light & {
      color: rgba(33, 37, 41, 0.9);
    }
  }

  p {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);

    .theme--light & {
      color: rgba(73, 80, 87, 0.7);
    }
  }
}

// Education Statistics
.education-stats {
  margin: 4rem 0;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 2rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.3s ease;

  .theme--light & {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(33, 150, 243, 0.08);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }
}

.stat-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);

  .theme--light & {
    background: rgba(33, 150, 243, 0.1);
  }
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 1.8rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1;
  margin-bottom: 0.3rem;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.stat-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;

  .theme--light & {
    color: rgba(73, 80, 87, 0.7);
  }
}

// Learning Goals
.learning-goals {
  margin: 4rem 0 2rem;
}

.goals-title {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }
}

.goals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.goal-card {
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 4px solid var(--goal-color);
  transition: all 0.3s ease;

  .theme--light & {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(33, 150, 243, 0.08);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-left-width: 6px;
  }
}

.goal-icon {
  margin-bottom: 1rem;
}

.goal-content {
  h4 {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.95);

    .theme--light & {
      color: rgba(33, 37, 41, 0.9);
    }
  }

  p {
    font-size: 0.85rem;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1rem;

    .theme--light & {
      color: rgba(73, 80, 87, 0.7);
    }
  }
}

.goal-progress {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.goal-percentage {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  min-width: 35px;

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }
}

// Responsive Design
@media (max-width: $breakpoint-md) {
  .education-section {
    padding: 2rem 0;
  }

  .section-container {
    padding: 0 1rem;
  }

  .modern-title {
    font-size: 2.5rem;
  }

  .timeline-container::before {
    left: 30px;
  }

  .education-item {
    &.alternate {
      flex-direction: row;

      .education-card {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }

  .stats-container {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
  }

  .goals-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $breakpoint-xs) {
  .modern-title {
    font-size: 2rem;
  }

  .modern-description {
    font-size: 1rem;
  }

  .education-card {
    margin-left: 0.5rem;
  }

  .card-header {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .card-body {
    padding: 1rem;
  }

  .degree-details {
    flex-direction: column;
    gap: 0.5rem;
  }

  .stat-card {
    padding: 1rem;
  }

  .goal-card {
    padding: 1rem;
  }
}

// Dark/Light theme specific adjustments
.theme--light {
  .education-card {
    &:hover {
      box-shadow: 0 12px 28px rgba(33, 150, 243, 0.15);
    }
  }

  .timeline-dot {
    &:hover {
      box-shadow: 0 8px 20px rgba(33, 150, 243, 0.3);
    }
  }

  .stat-card,
  .goal-card {
    &:hover {
      box-shadow: 0 12px 24px rgba(33, 150, 243, 0.1);
    }
  }
}
</style>